import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Stack,
  Tooltip,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Chip,
  Divider,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import useGentian from '../../core/hooks/useGentian';
import { ICredit, CreditChangeLogEntry } from '../../core/interfaces/credit.interface';
import { countryToISO } from '../../Utils/country.utils';

const ResellerDashboard = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const {
    useCreditByUserId,
    useAllProjects,
    createOrUpdateCredit,
    getResellers,
    notifyResellerLimit,
    useAccount,
    refundProject,
    useProjects,
  } = useGentian();

  const { data: projects, refetch: refetchProjects } = useProjects();

  // -------------------
  // Fetch data
  // -------------------
  const { data: credit, isLoading: creditLoading, refetch } = useCreditByUserId(userId!);
  const { data: projectsWithResult, isLoading: loadingWithResult } = useAllProjects({ status: 'has-result' });
  const { data: projectsWithoutResult, isLoading: loadingWithoutResult } = useAllProjects({
    status: 'no-result',
  });
  const { data: accountData } = useAccount();
  const { mutate, isLoading: saving } = createOrUpdateCredit;
  const { data: resellers } = getResellers;
  const { mutate: triggerNotifyResellerLimit, isLoading: notifyingLimit } = notifyResellerLimit;

  // -------------------
  // State
  // -------------------
  const [editedLimit, setEditedLimit] = useState<number | undefined>();
  const [editing, setEditing] = useState(false);
  const [saved, setSaved] = useState(false);
  const [resellerMetadata, setResellerMetadata] = useState<any>(null);
  const [notifyStatus, setNotifyStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [notifyMessage, setNotifyMessage] = useState('');
  const currentUserName =
    (typeof accountData?.data?.name === 'string' && accountData.data.name) ||
    (typeof accountData?.data?.user_metadata?.name === 'string' && accountData.data.user_metadata.name) ||
    undefined;
  const currentUserEmail = typeof accountData?.data?.email === 'string' ? accountData.data.email : undefined;

  // -------------------
  // Fetch reseller metadata
  // -------------------
  useEffect(() => {
    if (!resellers || !userId) return;
    const currentReseller = resellers.find((r: any) => r.user_id === userId);
    setResellerMetadata(currentReseller || null);
  }, [resellers, userId]);

  // -------------------
  // Derived data
  // -------------------
  const resellerProjects = useMemo(() => {
    if (!userId) return [];
    const identifiers = new Set(
      [userId, resellerMetadata?.email, resellerMetadata?.name, resellerMetadata?.user_metadata?.name]
        .filter((val): val is string => typeof val === 'string' && val.trim().length > 0)
        .map((val) => val.toLowerCase()),
    );

    const allProjects = [
      ...(projects || []),
      ...(projectsWithResult || []),
      ...(projectsWithoutResult || []),
    ];

    const filtered = allProjects.filter((project) => {
      const requestedBy = typeof project.requestedBy === 'string' ? project.requestedBy.toLowerCase() : '';
      const customerName = typeof project.customerName === 'string' ? project.customerName.toLowerCase() : '';
      const email = typeof project.email === 'string' ? project.email.toLowerCase() : '';
      return (
        (requestedBy && identifiers.has(requestedBy)) ||
        (customerName && identifiers.has(customerName)) ||
        (email && identifiers.has(email))
      );
    });

    const byId = new Map<string, any>();
    filtered.forEach((project) => {
      if (project?._id) byId.set(String(project._id), project);
    });
    return Array.from(byId.values());
  }, [
    projects,
    projectsWithResult,
    projectsWithoutResult,
    userId,
    resellerMetadata?.email,
    resellerMetadata?.name,
    resellerMetadata?.user_metadata?.name,
  ]);

  const debtLimit = editedLimit ?? credit?.debtLimit ?? 0;
  const available = debtLimit - (credit?.amountUsed ?? 0);
  const usagePercent = debtLimit > 0 ? Math.round(((credit?.amountUsed ?? 0) / debtLimit) * 100) : 0;
  const isLoading = creditLoading || loadingWithResult || loadingWithoutResult;

  const latestProjectId = useMemo(() => {
    if (!resellerProjects.length) return undefined;
    const withDate = resellerProjects.filter((p) => p?.requestDate);
    const list = withDate.length > 0 ? withDate : resellerProjects;
    const latest = list.reduce((acc: any, cur: any) => {
      const accTime = acc?.requestDate ? new Date(acc.requestDate).getTime() : 0;
      const curTime = cur?.requestDate ? new Date(cur.requestDate).getTime() : 0;
      return curTime > accTime ? cur : acc;
    }, list[0]);
    return latest?._id as string | undefined;
  }, [resellerProjects]);

  // -------------------
  // Handlers
  // -------------------
  const handleSave = () => {
    if (!credit || !resellerMetadata || !accountData?.data?.user_id) return;

    const updatedBy = typeof accountData?.data?.name === 'string' ? accountData.data.name : 'unknown';

    // Create payload with all required fields for backend + TS
    const payload: ICredit & { customerName: string; email: string } = {
      userId: credit.userId,
      debtLimit, // the new limit
      amountUsed: credit.amountUsed ?? 0,
      totalSpend: credit.totalSpend ?? 0,
      updatedBy,
      customerName: resellerMetadata.name || 'Unknown',
      email: resellerMetadata.email || '',
      // Optional fields the backend will handle automatically
      transactionLog: credit.transactionLog ?? [],
      changeLog: credit.changeLog ?? [],
    };

    mutate(payload, {
      onSuccess: () => {
        refetch(); // refresh data
        setEditing(false);
        setEditedLimit(undefined);
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      },
      onError: (err: any) => {
        alert(`Failed to update debt limit: ${err?.message || 'Unknown error'}`);
      },
    });
  };

  // Refund handler
  const handleRefund = (project: any) => {
    if (!project?.price) return;
    if (project.refunded) return;

    if (!window.confirm(`Refund £${project.price.toFixed(2)} for "${project.name}"?`)) return;

    const updatedBy = typeof accountData?.data?.name === 'string' ? accountData.data.name : 'unknown';

    refundProject.mutate(
      { projectId: project._id, updatedBy }, // <-- pass updatedBy here
      {
        onSuccess: () => {
          refetch();
          refetchProjects();
          alert(`Refunded £${project.price.toFixed(2)} successfully.`);
        },
        onError: () => {
          alert('Refund failed.');
        },
      },
    );
  };

  const handleNotifyResellerLimit = () => {
    const resellerEmail = resellerMetadata?.email;
    if (!latestProjectId || !resellerEmail) return;

    setNotifyStatus('idle');
    setNotifyMessage('');

    triggerNotifyResellerLimit(
      { projectId: latestProjectId, resellerEmail, usagePercent },
      {
        onSuccess: (data) => {
          const message =
            (data as { message?: string })?.message ||
            `Notification sent to ${resellerEmail} (${usagePercent}%).`;
          setNotifyStatus('success');
          setNotifyMessage(message);
        },
        onError: (err) => {
          setNotifyStatus('error');
          setNotifyMessage(err instanceof Error ? err.message : 'Failed to send notification.');
        },
      },
    );
  };

  // -------------------
  // Components
  // -------------------
  const CreditStat = ({ label, value, color = 'text.primary', editable, onChange }: any) => (
    <Stack spacing={0.5} sx={{ minWidth: 140 }}>
      <Typography variant="subtitle2" color="text.secondary">
        {label}
      </Typography>
      {editable ? (
        <TextField
          value={debtLimit}
          onChange={(e) => onChange?.(Number(e.target.value))}
          size="small"
          type="number"
          inputProps={{ style: { fontSize: 22, fontWeight: 600 } }}
        />
      ) : (
        <Typography variant="h4" fontWeight="bold" color={color}>
          {value}
        </Typography>
      )}
    </Stack>
  );

  const ResellerInfoCard = ({ metadata }: { metadata: any }) => {
    if (!metadata) return null;
    const userMeta = metadata.user_metadata || {};

    return (
      <Paper sx={{ p: 3, mb: 4 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={4} alignItems="center">
          <Avatar src={metadata.picture} alt={metadata.name || 'Reseller'} sx={{ width: 80, height: 80 }} />
          <Stack spacing={1} flex={1}>
            <Typography variant="h6">{metadata.name || userMeta.name || '—'}</Typography>
            <Typography variant="body2" color="text.secondary">
              {metadata.email || '—'}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {userMeta.company && <Chip label={userMeta.company} size="small" />}
              {userMeta.jobTitle && <Chip label={userMeta.jobTitle} size="small" color="primary" />}
              {userMeta.platformClient && <Chip label="Platform Client" size="small" color="success" />}
              {userMeta.country && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <img
                    src={`https://flagcdn.com/w20/${countryToISO(userMeta.country)}.png`}
                    srcSet={`https://flagcdn.com/w40/${countryToISO(userMeta.country)}.png 2x`}
                    alt={userMeta.country}
                    width={20}
                    height={15}
                    style={{ borderRadius: 2 }}
                  />
                  <Typography variant="body2">{userMeta.country}</Typography>
                </Stack>
              )}
            </Stack>
            <Divider sx={{ my: 1 }} />
            <Stack direction="row" spacing={2} flexWrap="wrap">
              {metadata.email_verified !== undefined && (
                <Chip
                  label={`Email Verified: ${metadata.email_verified ? 'Yes' : 'No'}`}
                  size="small"
                  color={metadata.email_verified ? 'success' : 'default'}
                />
              )}
              {metadata.logins_count !== undefined && (
                <Chip label={`Logins: ${metadata.logins_count}`} size="small" />
              )}
              {metadata.last_login && (
                <Chip
                  label={`Last Login: ${new Date(metadata.last_login).toLocaleDateString()}`}
                  size="small"
                />
              )}
            </Stack>
          </Stack>
        </Stack>
      </Paper>
    );
  };

  if (!userId || !credit) return null;

  const displayUpdatedBy = (value?: string) => {
    if (!value) return '—';
    if (currentUserName && (value === currentUserEmail || value === accountData?.data?.user_id)) {
      return currentUserName;
    }
    if (currentUserName && resellerMetadata?.email && value === resellerMetadata.email) {
      return currentUserName;
    }
    return value;
  };

  // 30 days ago date for refund eligibility
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

  // -------------------
  // Render
  // -------------------
  return (
    <Container maxWidth="xl" sx={{ mt: 4 }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/reseller/admin-panel')}
        >
          Admin Panel
        </Button>
        <Stack spacing={0.5}>
          <Typography variant="h4" fontWeight="bold">
            Reseller Dashboard
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              User ID:
            </Typography>
            <Tooltip title="Copy User ID">
              <Typography
                variant="body2"
                sx={{ fontFamily: 'monospace', cursor: 'pointer' }}
                onClick={() => navigator.clipboard.writeText(userId)}
              >
                {userId}
              </Typography>
            </Tooltip>
          </Stack>
        </Stack>
      </Stack>

      {/* Info */}
      <Alert severity="info" sx={{ mb: 3 }}>
        Viewing orders placed by this reseller
      </Alert>

      {/* Reseller Metadata Card */}
      <ResellerInfoCard metadata={resellerMetadata} />

      {/* Credit Management */}
      <Paper sx={{ p: 4, mb: 4, width: '100%' }}>
        {creditLoading ? (
          <CircularProgress />
        ) : (
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={4}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            justifyContent="space-between"
          >
            <CreditStat
              label="Debt Limit"
              value={`£${debtLimit}`}
              editable={editing}
              onChange={(val: number) => setEditedLimit(val)}
            />
            <CreditStat
              label="Used"
              value={`£${Number(credit.amountUsed ?? 0).toFixed(2)}`}
              color="error.main"
            />
            <CreditStat
              label="Available"
              value={`£${Number(available).toFixed(2)}`}
              color={available < 0 ? 'error.main' : 'success.main'}
            />
            <CreditStat label="Total Spend" value={`£${Number(credit.totalSpend ?? 0).toFixed(2)}`} />
            <Stack direction="row" spacing={1}>
              {editing ? (
                <>
                  <Button variant="contained" size="medium" onClick={handleSave} disabled={saving}>
                    {saving ? <CircularProgress size={18} /> : 'Save'}
                  </Button>
                  <Button
                    variant="outlined"
                    size="medium"
                    color="secondary"
                    onClick={() => {
                      setEditedLimit(undefined);
                      setEditing(false);
                    }}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                </>
              ) : (
                <Button variant="outlined" size="medium" onClick={() => setEditing(true)}>
                  Edit Limit
                </Button>
              )}
            </Stack>
          </Stack>
        )}
        {saved && (
          <Typography color="success.main" sx={{ mt: 1 }}>
            ✅ Saved successfully
          </Typography>
        )}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 2 }} alignItems="center">
          <Typography variant="body2" color="text.secondary">
            Usage: {usagePercent}%
          </Typography>
          <Button
            variant="contained"
            size="small"
            onClick={handleNotifyResellerLimit}
            disabled={notifyingLimit || !latestProjectId || !resellerMetadata?.email}
          >
            {notifyingLimit ? <CircularProgress size={16} /> : 'Send 80% Usage Email'}
          </Button>
        </Stack>
        {notifyStatus !== 'idle' && (
          <Typography
            sx={{ mt: 1 }}
            color={notifyStatus === 'success' ? 'success.main' : 'error.main'}
            fontSize={14}
          >
            {notifyMessage}
          </Typography>
        )}
      </Paper>

      {/* Change Log Table */}
      <Typography variant="h6" gutterBottom>
        Debt Limit Change Log
      </Typography>
      <Paper sx={{ p: 2, mb: 4 }}>
        {!credit.changeLog || credit.changeLog.length === 0 ? (
          <Typography color="text.secondary">No changes yet.</Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Previous (£)</TableCell>
                  <TableCell>New (£)</TableCell>
                  <TableCell>Updated By</TableCell>
                  <TableCell>Updated At</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {credit.changeLog.map((log, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{log.previous}</TableCell>
                    <TableCell>{log.newValue}</TableCell>
                    <TableCell>{displayUpdatedBy(log.updatedBy)}</TableCell>
                    <TableCell>{new Date(log.updatedAt).toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Orders Table */}
      <Typography variant="h6" gutterBottom>
        Orders / Projects
      </Typography>
      <Paper sx={{ p: 2, mb: 4 }}>
        {isLoading ? (
          <Stack alignItems="center" py={4}>
            <CircularProgress />
          </Stack>
        ) : resellerProjects.length === 0 ? (
          <Typography color="text.secondary">No orders found for this reseller.</Typography>
        ) : (
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Project</TableCell>
                  <TableCell>Product</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Area (ha)</TableCell>
                  <TableCell align="right">Cost (£)</TableCell>
                  <TableCell>Refund</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {resellerProjects.map((project) => {
                  const refundEligible =
                    project.requestDate && new Date(project.requestDate) >= thirtyDaysAgo;

                  return (
                    <TableRow key={project._id} hover>
                      <TableCell sx={{ fontFamily: 'monospace' }}>
                        {project._id?.toString().slice(-6)}
                      </TableCell>
                      <TableCell>{project.name || '—'}</TableCell>
                      <TableCell>{project.products?.[0]?.name || '—'}</TableCell>
                      <TableCell>
                        {project.requestDate ? new Date(project.requestDate).toLocaleDateString() : '—'}
                      </TableCell>
                      <TableCell>{project.status || 'N/A'}</TableCell>
                      <TableCell>{project.totalArea ?? '—'}</TableCell>
                      <TableCell align="right">£ {project.price?.toFixed(2) ?? '—'}</TableCell>
                      <TableCell>
                        {project.refunded ? (
                          <Chip label="Refunded" color="success" size="small" />
                        ) : refundEligible && project.price ? (
                          <Button
                            variant="outlined"
                            color="secondary"
                            size="small"
                            onClick={() => handleRefund(project)}
                          >
                            Refund
                          </Button>
                        ) : (
                          <Typography variant="body2" color="text.disabled">
                            Refund expired
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Transaction Log */}
      <Typography variant="h6" gutterBottom>
        Transaction Log
      </Typography>
      <Paper sx={{ p: 2, mb: 4 }}>
        {!credit.transactionLog || credit.transactionLog.length === 0 ? (
          <Typography color="text.secondary">No transactions yet.</Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell>Amount (£)</TableCell>
                  <TableCell>Stripe Session</TableCell>
                  <TableCell>Stripe Invoice</TableCell>
                  <TableCell>Note</TableCell>
                  <TableCell>Created At</TableCell>
                  <TableCell>Created By</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {credit.transactionLog
                  .slice()
                  .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()) // newest first
                  .map((tx, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{tx.type}</TableCell>
                      <TableCell>£ {tx.amount.toFixed(2)}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>
                        {tx.stripeSessionId?.slice(-6) || '—'}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>
                        {tx.stripeInvoiceId?.slice(-6) || '—'}
                      </TableCell>
                      <TableCell>{tx.note || '—'}</TableCell>
                      <TableCell>{new Date(tx.createdAt).toLocaleString()}</TableCell>
                      <TableCell>{tx.createdBy || '—'}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Container>
  );
};

export default ResellerDashboard;
